import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "@phosphor-icons/react";
import { api, fileUrl } from "../../lib/api";
import { subscribeContentUpdates } from "../../lib/contentSync";

const RANK_CLS = ["text-[#FFB800]", "text-racing-muted", "text-[#B0763F]"];

export default function CategoryScoreboard({ category }) {
    const [year, setYear] = useState(0);
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        const load = () => {
            api.get("/scoreboard", { params: { category, year: year || undefined } })
                .then((r) => { if (active) setData(r.data); })
                .catch(() => { if (active) setData({ category, year, years: [], ranking: [] }); });
        };
        load();
        const unsubscribe = subscribeContentUpdates(load);
        return () => { active = false; unsubscribe(); };
    }, [category, year]);

    const years = data?.years || [];
    const selectedYear = data?.year || year;
    const ranking = data?.ranking || [];

    return (
        <section className="mt-10 border border-racing-line bg-white" data-testid="category-scoreboard">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-racing-line bg-racing-red px-5 py-4">
                <div className="flex items-center gap-3">
                    <Trophy size={22} weight="duotone" className="text-racing-gold" />
                    <div>
                        <h2 className="font-display uppercase text-2xl text-white" data-testid="category-scoreboard-title">Papan Skor {category}</h2>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-white/70">Klik nama atlet untuk melihat seluruh prestasinya</p>
                    </div>
                </div>
                <select
                    value={selectedYear}
                    onChange={(e) => setYear(Number(e.target.value))}
                    data-testid="category-scoreboard-year"
                    className="bg-white border border-white/40 px-4 py-2.5 text-sm text-neutral-900 focus:outline-none"
                >
                    {years.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
            </div>

            {!data ? (
                <p className="p-6 text-sm text-racing-muted" data-testid="category-scoreboard-loading">Memuat papan skor...</p>
            ) : ranking.length === 0 ? (
                <p className="p-6 text-sm text-racing-muted" data-testid="category-scoreboard-empty">Belum ada medali kategori {category} pada tahun {selectedYear}.</p>
            ) : (
                <div data-testid="category-scoreboard-table">
                    <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-racing-line bg-racing-surface text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                        <span className="col-span-1">#</span>
                        <span className="col-span-6">Atlet</span>
                        <span className="col-span-2 text-center text-[#B8890B]">Emas</span>
                        <span className="col-span-2 text-center">Perak</span>
                        <span className="col-span-1 text-center text-[#B0763F]">Perunggu</span>
                    </div>
                    {ranking.map((entry, index) => (
                        <div key={entry.name} className="grid grid-cols-12 gap-2 items-center px-5 py-4 border-b border-racing-line last:border-0 hover:bg-black/[0.02]" data-testid={`category-scoreboard-row-${index}`}>
                            <span className={`col-span-1 font-display text-2xl ${RANK_CLS[index] || "text-black/25"}`}>{String(index + 1).padStart(2, "0")}</span>
                            <span className="col-span-6 min-w-0 flex items-center gap-3">
                                {entry.photo_url ? (
                                    <img src={fileUrl(entry.photo_url)} alt={entry.name} className="w-10 h-10 object-cover border border-racing-line shrink-0" data-testid={`category-scoreboard-photo-${index}`} />
                                ) : (
                                    <span className="w-10 h-10 bg-racing-surface border border-racing-line flex items-center justify-center font-display text-racing-red shrink-0">{entry.name.charAt(0)}</span>
                                )}
                                {entry.athlete_id ? (
                                    <Link to={`/atlet/${entry.athlete_id}`} data-testid={`category-scoreboard-athlete-${index}`} className="font-medium uppercase text-sm text-neutral-900 hover:text-racing-red transition-colors truncate">
                                        {entry.name}
                                    </Link>
                                ) : (
                                    <span className="font-medium uppercase text-sm text-neutral-900 truncate">{entry.name}</span>
                                )}
                            </span>
                            <span className="col-span-2 text-center font-display text-xl text-[#B8890B]">{entry.gold}</span>
                            <span className="col-span-2 text-center font-display text-xl text-racing-muted">{entry.silver}</span>
                            <span className="col-span-1 text-center font-display text-xl text-[#B0763F]">{entry.bronze}</span>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
